import React, { useEffect, useState, useMemo } from "react";
import { 
    Box, Button, Container, Grid, Card, Typography, Dialog, DialogTitle, 
    DialogContent, DialogActions, TextField, useTheme, Tabs, Tab, 
    FormControl, InputLabel, Select, MenuItem, Divider, Stack, Avatar
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Iconos
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faCheckCircle, faExclamationCircle, faPlus, faDoorOpen } from "@fortawesome/free-solid-svg-icons";

// Componentes QUD & Controllers
import WeeklyScheduleEditor from "../../../components/schedules/WeeklyScheduleEditor"; 
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import QUDProgress from "../../../components/progress";
import ResponseComponent from "../../../components/responseComponent";
import { getAllAssets, createAsset, updateAsset, deleteAsset } from '../../../controllers/company/AssetsController';
import AssetsDataTable from "./AssetsDataTable";

// Estado inicial
const initialForm = { id: null, name: '', capacity: '', status: 1 };

export default function AssetsManager() {
    const theme = useTheme();
    
    // Estados
    const [assets, setAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Modal
    const [openDialog, setOpenDialog] = useState(false);
    const [tabValue, setTabValue] = useState(0); 
    const [form, setForm] = useState(initialForm);
    const [isSaving, setIsSaving] = useState(false);

    // Feedback
    const [response, setResponse] = useState({ show: false, success: false, message: '' });

    useEffect(() => { 
        window.scrollTo(0, 0);
        handleGetAssets(); 
    }, []);

    const handleGetAssets = async () => {
        setIsLoading(true);
        const {success, message, assets} = await getAllAssets();
        if (success) setAssets(assets || []);
        setIsLoading(false);
    };

    // KPIs
    const stats = useMemo(() => {
        const total = assets.length;
        const active = assets.filter(a => a.status === 1).length;
        return { total, active, inactive: total - active };
    }, [assets]);

    // --- MANEJADORES ---
    const handleOpen = (asset = null) => {
        if (asset) {
            setForm({ 
                id: asset.id, 
                name: asset.name, 
                capacity: asset.capacity, 
                status: asset.status 
            });
        } else {
            setForm(initialForm);
        }
        setTabValue(0);
        setOpenDialog(true);
    };

    const handleClose = () => setOpenDialog(false);

    const handleSave = async () => {
        setIsSaving(true);
        let res;

        if (form.id) {
            res = await updateAsset(form.id, form);
        } else {
            res = await createAsset(form);
        }

        setIsSaving(false);
        
        if (res.success) {
            setResponse({ show: true, success: true, message: res.message || 'Guardado correctamente' });
            if (!form.id) handleClose();
            handleGetAssets(); // Recargar para obtener ID si fue nuevo
        } else {
            setResponse({ show: true, success: false, message: res.message || 'Error' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Eliminar este activo?")) return;
        
        setIsLoading(true);
        const res = await deleteAsset(id);
        
        if (res.success) {
            setAssets(prev => prev.filter(a => a.id !== id));
        } else {
            setResponse({ show: true, success: false, message: res.message });
        }
        setIsLoading(false);
    };

    // Estilo base para las tarjetas KPI
    const cardStyle = {
        elevation: 0,
        sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: 2.5, bgcolor: '#ffffff' }
    };

    return (
        <Container maxWidth='xl' sx={{ mt: 4, mb: 8 }}>
            <QUDProgress open={isLoading && assets.length === 0} />

            <CustomBreadcrumbs 
                returnTo='/dashboard' 
                crumbs={[{ name: 'Recursos Físicos', link: '/assets' }]}
            />
            
            {/* --- HEADER PRINCIPAL --- */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#222' }}>
                        Panel de Activos
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Administra espacios físicos, mesas, canchas y recursos reservables.
                    </Typography>
                </Box>
                
                <Button
                    variant='contained'
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => handleOpen()}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
                >
                    Agregar Activo
                </Button>
            </Box>

            {/* --- CABECERA DE ESTADÍSTICAS (KPIs) --- */}
            {assets.length > 0 && (
                <Grid container spacing={3} mb={4}>
                    {/* KPI 1: Total */}
                    <Grid item xs={12} sm={4}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faBuilding} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Total de Recursos
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.total}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 2: Disponibles */}
                    <Grid item xs={12} sm={4}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faCheckCircle} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Disponibles
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.active}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 3: Fuera de Servicio */}
                    <Grid item xs={12} sm={4}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faExclamationCircle} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Fuera de Servicio
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.inactive}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* --- CONTENIDO PRINCIPAL --- */}
            {assets.length !== 0 ? (
                <AssetsDataTable
                    data={assets} 
                    isLoading={isLoading} 
                    onEdit={handleOpen} 
                    onDelete={handleDelete} 
                />
            ) : (
                !isLoading && (
                    /* EMPTY STATE */
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 3, boxShadow: 'none', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Box sx={{ width: 80, height: 80, bgcolor: '#f8f9fa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                            <FontAwesomeIcon icon={faDoorOpen} size="3x" color="#ccc" />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#333' }} gutterBottom>
                            Aún no tienes recursos registrados
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', maxWidth: 450, mb: 4 }}>
                            Registra tus canchas, salas, mesas o cualquier espacio físico para que tus clientes puedan comenzar a reservarlos.
                        </Typography>
                        <Button
                            variant='outlined'
                            color="primary"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => handleOpen()}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                            Crear mi primer recurso
                        </Button>
                    </Card>
                )
            )}

            {/* --- MODAL (NUEVO / EDITAR) --- */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                        {form.id ? 'Gestionar Recurso' : 'Nuevo Recurso Físico'}
                    </Typography>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 3 }}>
                    <Tabs value={tabValue} onChange={(e,v)=>setTabValue(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', minHeight: 40 }} indicatorColor="primary">
                        <Tab label="Información" icon={<InfoIcon/>} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 'bold' }}/>
                        <Tab label="Disponibilidad" icon={<AccessTimeIcon/>} iconPosition="start" disabled={!form.id} sx={{ textTransform: 'none', fontWeight: 'bold' }}/>
                    </Tabs>

                    {tabValue === 0 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Nombre del Activo"
                                    placeholder="Ej: Cancha 1, Sala de Conferencias"
                                    fullWidth size="small"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Capacidad"
                                    type="number"
                                    fullWidth size="small"
                                    value={form.capacity}
                                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                                    InputProps={{ endAdornment: <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Pers.</Typography> }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Estado</InputLabel>
                                    <Select 
                                        value={form.status} 
                                        label="Estado" 
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    >
                                        <MenuItem value={1}>Disponible</MenuItem>
                                        <MenuItem value={0}>Fuera de Servicio</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {tabValue === 1 && form.id && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Define los horarios en los que este recurso puede ser reservado.
                            </Typography>
                            <WeeklyScheduleEditor 
                                resourceType="asset" 
                                resourceId={form.id} 
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
                    <Button onClick={handleClose} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 'bold' }}>
                        Cancelar
                    </Button>
                    {tabValue === 0 && (
                        <LoadingButton 
                            onClick={handleSave} 
                            variant="contained" 
                            loading={isSaving}
                            disableElevation
                            sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                        >
                            {form.id ? 'Guardar Cambios' : 'Crear Recurso'}
                        </LoadingButton>
                    )}
                </DialogActions>
            </Dialog>

            <ResponseComponent show={response.show} success={response.success} message={response.message} onClick={() => setResponse({...response, show: false})} />

        </Container>
    );
}